import React, { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import cloneDeep from 'lodash.clonedeep';
import Select from 'react-select';

// filters
import * as filterHelper from '/src/lib/filterHelper';
import { initialFilters } from './helpers/filterState';

// react select
import { sharedConstants } from 'shared-helpers';
const reactSelectLarge = sharedConstants.reactSelectLarge;
const reactSelectTheme = sharedConstants.reactSelectTheme;

// ForwardRef is used to access the component method in parent
const Filters = forwardRef(
    (
        {
            location,
            countData,
            filterFields,
            filterValues,
            setCountData,
            setFilterFields,
            taxonomyList,
        },
        ref,
    ) => {
        const [keyword, setKeyword] = useState('');
        const [country, setCountry] = useState('');
        const [industry, setIndustry] = useState('');
        const [category, setCategory] = useState('');

        const [checkboxes, setCheckboxes] = useState({
            type_id: { '0^;2': false, 1: false },
            lang: { ar: false, en: false },
        });

        useEffect(() => {
            let url = location.search;
            let newSavedFilters = filterHelper.convertToFilterString(url);

            // handle filtered url
            if (newSavedFilters) {
                applySavedFilters(newSavedFilters);
            } else {
                filterValues({ filterFields });
            }
        }, [location.search]);

        /**
         * Method to apply url filters
         *
         * first it will decode the url, then will extract the different filters
         * set the new filter values to the appropriate state fields
         * then get the filtered data
         */
        const applySavedFilters = (savedFilters) => {
            let filterArr = filterHelper.decodeFilters(savedFilters);
            let allFilters = {};
            initialFilters.map((x) => {
                const key = x.key == 'magnitt_industry' ? 'industry' : x.key;
                allFilters[x.key] = filterHelper.getFilterByKey(filterArr, key, [], true);
            });

            // set the languages checkboxes
            if (allFilters.lang && allFilters.lang.length > 0) {
                const updatedCheckboxes = cloneDeep(checkboxes);
                allFilters.lang.forEach((lang) => {
                    updatedCheckboxes.lang[lang] = true;
                });
                setCheckboxes(updatedCheckboxes);
            }

            let langNames = allFilters.lang
                ? allFilters.lang.map((l) => (l === 'ar' ? 'Arabic' : l === 'en' ? 'English' : ''))
                : [];

            // set the types checkboxes
            if (allFilters.type_id && allFilters.type_id.length > 0) {
                const updatedCheckboxes = cloneDeep(checkboxes);
                allFilters.type_id.forEach((type) => {
                    if (type === '0' || type === '2') {
                        updatedCheckboxes.type_id['0^;2'] = true;
                    } else if (type === '1') {
                        updatedCheckboxes.type_id['1'] = true;
                    }
                });
                setCheckboxes(updatedCheckboxes);
            }

            let typeNames = allFilters.type_id
                ? allFilters.type_id.map((t) =>
                    t === '0' || t === '2' ? 'Free' : t === '1' ? 'Paid' : '',
                )
                : [];

            // set the filters fields values
            const copyFilterFields = cloneDeep(filterFields);
            copyFilterFields.map((item) => {
                switch (item.key) {
                case 'keyword':
                case 'category':
                case 'tag':
                case 'location_id':
                case 'magnitt_industry':
                    const val = allFilters[item.key];
                    item.value = val !== undefined ? val : [];
                    break;
                case 'lang':
                    item.value = langNames;
                    break;
                case 'type_id':
                    item.value = [...new Set(typeNames)]; // to remove duplicates
                    break;
                default:
                    break;
                }
            });

            setFilterFields({ type: 'UPDATE_STATE', payload: copyFilterFields });
            filterValues({ filterFields: copyFilterFields });
        };

        const handleKeyword = (e) => {
            const keyCode = e !== 'submit' ? e.keyCode : null;
            if ((keyCode && keyCode === 13) || e == 'submit') {
                if (keyword !== '') {
                    filterFields.map((item) => {
                        if (item.key === 'keyword' && item.value.indexOf(keyword) === -1) {
                            item.value = [keyword];
                        }
                    });

                    setFilterFields({ type: 'UPDATE_STATE', payload: filterFields });
                    filterValues({ filterFields });
                }
            }
        };

        const handleSelectChange = (option, type) => {
            const tempFilterFields = [...filterFields];
            tempFilterFields.map((filter) => {
                if (filter.key == type) {
                    filter.value = [option.value];
                }
            });

            if (type == 'location_id') {
                setCountry(option);
            } else if (type == 'magnitt_industry') {
                setIndustry(option);
            } else if (type == 'category') {
                setCategory(option);
            }

            setFilterFields({ type: 'UPDATE_STATE', payload: tempFilterFields });
            filterValues({ filterFields: tempFilterFields });
        };

        // To handle checkbox filters
        const handleCBFilter = (e, options = {}) => {
            const copyFilterFields = [...filterFields];
            const { name, value, checked } = e.target;
            const copyCheckboxes = cloneDeep(checkboxes);
            const cbState = copyCheckboxes[name];

            copyFilterFields.map((item) => {
                if (item.key === name) {
                    if (checked) {
                        item.value.push(value);
                    } else {
                        item.value = item.value.filter((filteredValue) => filteredValue !== value);
                    }
                }
            });

            cbState[value] = checked;
            copyCheckboxes[name] = cbState;
            setFilterFields({ type: 'UPDATE_STATE', payload: filterFields });
            setCheckboxes(copyCheckboxes);
            filterValues({ filterFields });
        };

        const clearFilter = () => {
            setKeyword('');
            setCountry('');
            setIndustry('');
            setCategory('');
            setFilterFields({ type: 'RESET' });
            setCountData({ limit: 6 });
            setCheckboxes({
                type_id: { '0^;2': false, 1: false },
                lang: { ar: false, en: false },
            });
            filterValues({ filterFields: cloneDeep(initialFilters) });
        };

        // useImperativeHandle customizes the instance value that is exposed to parent components when using ref
        // Clearing filters from parent component
        useImperativeHandle(ref, () => ({
            clearFilter: () => clearFilter(),
        }));

        const inputStyles = {
            ...reactSelectLarge,
            placeholder: (base) => ({ ...base, color: '#625f5f' })
        }

        return (
            <form className="search-row m-0 research-filters" onSubmit={(e) => e.preventDefault()}>
                <div className="col-12 col-md-8 col-lg-9 col-xl-8 p-0">
                    {/* searchbar, keyword */}
                    <div className="row m-0 d-flex flex-row flex-nowrap">
                        <div className="col-9 form-control clear-outline">
                            <div className="input-container">
                                <input
                                    type="text"
                                    autoComplete="off"
                                    placeholder={'Search reports e.g. Fintech in MENA'}
                                    value={keyword}
                                    onKeyDown={(e) => handleKeyword(e)}
                                    onChange={(e) => setKeyword(e.target.value)}
                                />
                            </div>
                        </div>
                        <button
                            className="col-3 btn btn-main a-tag-style"
                            type="button"
                            onClick={() => handleKeyword('submit')}
                            aria-label="submit-filter"
                        >
                            <i className="fas fa-search"></i>
                        </button>
                    </div>
                </div>

                <div className="col-12 p-0">
                    <div className="row research-select-row">
                        {/* country */}
                        <Select
                            theme={reactSelectTheme}
                            styles={inputStyles}
                            placeholder="Country"
                            className="col-6 col-md-4 filter-select mt-3"
                            options={taxonomyList.countries}
                            value={country}
                            onChange={(e) => handleSelectChange(e, 'location_id')}
                            aria-label="Country Selector"
                        />

                        {/* industry */}
                        <Select
                            theme={reactSelectTheme}
                            styles={inputStyles}
                            placeholder="Industry"
                            className="col-6 col-md-4 filter-select mt-3 p-0"
                            options={taxonomyList.industries}
                            value={industry}
                            onChange={(e) => handleSelectChange(e, 'magnitt_industry')}
                            aria-label="Industry Selector"
                        />

                        {/* category */}
                        <Select
                            theme={reactSelectTheme}
                            styles={inputStyles}
                            placeholder="Category"
                            className="col-6 col-md-4 filter-select mt-3 p-0"
                            options={taxonomyList.categories}
                            value={category}
                            onChange={(e) => handleSelectChange(e, 'category')}
                            aria-label="Category Selector"
                        />
                    </div>

                    <div className="row no-gutters">
                        {/* price */}
                        <div className="col-auto mt-3 pr-3">
                            <div className="mb-2">Type</div>
                            <div className="d-flex">
                                <div className="custom-control custom-checkbox">
                                    <input
                                        className="custom-control-input"
                                        type="checkbox"
                                        name="type_id"
                                        value="0^;2"
                                        id="priceCheck1"
                                        onChange={(e) => handleCBFilter(e, { label: 'Free' })}
                                        checked={checkboxes.type_id['0^;2']}
                                    />
                                    <label className="custom-control-label" htmlFor="priceCheck1">
                                        Free Reports <span> ({countData.specificCounts.free_count})</span>
                                    </label>
                                </div>
                                <div className="custom-control custom-checkbox">
                                    <input
                                        className="custom-control-input"
                                        type="checkbox"
                                        name="type_id"
                                        value="1"
                                        id="priceCheck2"
                                        onChange={(e) => handleCBFilter(e, { label: 'Paid' })}
                                        checked={checkboxes.type_id['1']}
                                    />
                                    <label className="custom-control-label" htmlFor="priceCheck2">
                                        Paid Reports <span> ({countData.specificCounts.paid_count})</span>
                                    </label>
                                </div>
                            </div>
                        </div>

                        {/* language */}
                        <div className="col-auto mt-3">
                            <div className="mb-2">Language</div>
                            <div className="d-flex">
                                <div className="custom-control custom-checkbox">
                                    <input
                                        className="custom-control-input"
                                        type="checkbox"
                                        name="lang"
                                        value="en"
                                        id="langCheck1"
                                        onChange={(e) => handleCBFilter(e, { label: 'English' })}
                                        checked={checkboxes.lang['en']}
                                    />
                                    <label className="custom-control-label" htmlFor="langCheck1">
                                        English <span> ({countData.specificCounts.en_count})</span>
                                    </label>
                                </div>
                                <div className="custom-control custom-checkbox">
                                    <input
                                        className="custom-control-input"
                                        type="checkbox"
                                        name="lang"
                                        value="ar"
                                        id="langCheck2"
                                        onChange={(e) => handleCBFilter(e, { label: 'Arabic' })}
                                        checked={checkboxes.lang['ar']}
                                    />
                                    <label className="custom-control-label" htmlFor="langCheck2">
                                        Arabic <span> ({countData.specificCounts.ar_count})</span>
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="research-filters__clear">
                        < button className="research-filters__clear-anchor a-tag-style" onClick={() => clearFilter()}  type="button">
                            Clear filters
                        </button>
                    </div>
                </div>
            </form>
        );
    },
);

export default Filters;
